<template>
  <div class="br-kpi-grid br-kpi-grid--3 mb-4">
    <!-- Card 1: Total Income -->
    <v-card class="br-kpi-card" flat>
      <v-card-text class="d-flex align-center pa-3">
        <div class="br-kpi-icon br-kpi-icon--income mr-3">
          <v-icon size="20" color="#10b981">fal fa-arrow-down</v-icon>
        </div>
        <div class="br-kpi-body">
          <div class="d-flex align-center justify-space-between">
            <div class="br-kpi-label">{{ $t("income") }}</div>
            <v-btn
              x-small
              depressed
              class="exp-quick-add-btn exp-quick-add-btn--income"
              @click="$emit('addNewExpense', 'income')"
            >
              <v-icon left x-small>fal fa-plus</v-icon>
              {{ $t("income") }}
            </v-btn>
          </div>
          <div
            class="br-kpi-value br-kpi-value--income"
            :class="{ 'br-kpi-value--dark': $vuetify.theme.dark }"
          >
            +{{ incomeTotal | NumberFormat }}
            <span class="text-caption grey--text font-weight-medium">LAK</span>
          </div>
          <div class="br-kpi-note">
            <v-icon size="11" class="mr-1" color="#10b981">fal fa-calendar-alt</v-icon>
            <span>{{ monthDisplay }}</span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Card 2: Total Expense -->
    <v-card class="br-kpi-card" flat>
      <v-card-text class="d-flex align-center pa-3">
        <div class="br-kpi-icon br-kpi-icon--expense mr-3">
          <v-icon size="20" color="#ef4444">fal fa-arrow-up</v-icon>
        </div>
        <div class="br-kpi-body">
          <div class="d-flex align-center justify-space-between">
            <div class="br-kpi-label">{{ $t("expense") }}</div>
            <v-btn
              x-small
              depressed
              class="exp-quick-add-btn exp-quick-add-btn--expense"
              @click="$emit('addNewExpense', 'expense')"
            >
              <v-icon left x-small>fal fa-plus</v-icon>
              {{ $t("expense") }}
            </v-btn>
          </div>
          <div
            class="br-kpi-value br-kpi-value--expense"
            :class="{ 'br-kpi-value--dark': $vuetify.theme.dark }"
          >
            -{{ expenseTotal | NumberFormat }}
            <span class="text-caption grey--text font-weight-medium">LAK</span>
          </div>
          <div class="br-kpi-note">
            <v-icon size="11" class="mr-1" color="#ef4444">fal fa-calendar-alt</v-icon>
            <span>{{ monthDisplay }}</span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Card 3: Net Profit / Net Balance -->
    <v-card class="br-kpi-card" flat>
      <v-card-text class="d-flex align-center pa-3">
        <div
          class="br-kpi-icon mr-3"
          :class="netBalance >= 0 ? 'br-kpi-icon--net-pos' : 'br-kpi-icon--net-neg'"
        >
          <v-icon size="20" :color="netBalance >= 0 ? '#5b39c9' : '#f59e0b'">
            {{ netBalance >= 0 ? 'fal fa-wallet' : 'fal fa-exclamation-triangle' }}
          </v-icon>
        </div>
        <div class="br-kpi-body">
          <div class="d-flex align-center justify-space-between">
            <div class="br-kpi-label">{{ $t("profit") }} ({{ $t("net") }})</div>
            <v-chip
              x-small
              label
              :color="netBalance >= 0 ? 'success' : 'error'"
              outlined
              class="font-weight-bold"
            >
              {{ netBalance >= 0 ? 'Surplus' : 'Deficit' }}
            </v-chip>
          </div>
          <div
            class="br-kpi-value"
            :class="[
              netBalance >= 0 ? 'br-kpi-value--net-pos' : 'br-kpi-value--net-neg',
              { 'br-kpi-value--dark': $vuetify.theme.dark }
            ]"
          >
            {{ netBalance >= 0 ? '+' : '' }}{{ netBalance | NumberFormat }}
            <span class="text-caption grey--text font-weight-medium">LAK</span>
          </div>
          <div class="br-kpi-note">
            <span>{{ $t("income") }} - {{ $t("expense") }}</span>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { myMixin } from "@/mixin/main";

export default {
  name: "AppExpenseCard",
  props: {
    expense: {
      type: Array,
      default: () => [],
    },
  },
  mixins: [myMixin],
  computed: {
    incomeItem() {
      return this.expense.find((item) => item.name === "income") || null;
    },
    expenseItem() {
      return this.expense.find((item) => item.name === "expense") || null;
    },
    incomeTotal() {
      return this.incomeItem ? Number(this.incomeItem.total || 0) : 0;
    },
    expenseTotal() {
      return this.expenseItem ? Number(this.expenseItem.total || 0) : 0;
    },
    netBalance() {
      return this.incomeTotal - this.expenseTotal;
    },
    monthDisplay() {
      if (this.incomeItem && this.incomeItem.created_at) {
        return this.incomeItem.created_at;
      }
      if (this.expenseItem && this.expenseItem.created_at) {
        return this.expenseItem.created_at;
      }
      const now = new Date();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      return `${m}-${now.getFullYear()}`;
    },
  },
};
</script>

<style scoped>
.br-kpi-icon--income {
  background: rgba(16, 185, 129, 0.12) !important;
}

.br-kpi-icon--expense {
  background: rgba(239, 68, 68, 0.12) !important;
}

.br-kpi-icon--net-pos {
  background: rgba(91, 57, 201, 0.12) !important;
}

.br-kpi-icon--net-neg {
  background: rgba(245, 158, 11, 0.12) !important;
}

.br-kpi-value--income {
  color: #059669;
}

.br-kpi-value--expense {
  color: #dc2626;
}

.br-kpi-value--net-pos {
  color: #5b39c9;
}

.br-kpi-value--net-neg {
  color: #d97706;
}

.exp-quick-add-btn {
  font-size: 11px !important;
  font-weight: 700 !important;
  border-radius: 6px !important;
  padding: 0 8px !important;
  height: 22px !important;
  text-transform: none !important;
}

.exp-quick-add-btn--income {
  background: rgba(16, 185, 129, 0.12) !important;
  color: #059669 !important;
}

.exp-quick-add-btn--income:hover {
  background: rgba(16, 185, 129, 0.22) !important;
}

.exp-quick-add-btn--expense {
  background: rgba(239, 68, 68, 0.12) !important;
  color: #dc2626 !important;
}

.exp-quick-add-btn--expense:hover {
  background: rgba(239, 68, 68, 0.22) !important;
}

.theme--dark .br-kpi-value--income {
  color: #34d399 !important;
}

.theme--dark .br-kpi-value--expense {
  color: #f87171 !important;
}

.theme--dark .br-kpi-value--net-pos {
  color: #a5b4fc !important;
}

.theme--dark .br-kpi-value--net-neg {
  color: #fbbf24 !important;
}

.theme--dark .exp-quick-add-btn--income {
  background: rgba(16, 185, 129, 0.18) !important;
  color: #34d399 !important;
}

.theme--dark .exp-quick-add-btn--expense {
  background: rgba(239, 68, 68, 0.18) !important;
  color: #f87171 !important;
}
</style>
